import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToastStore } from '@/shared/stores/toastStore';
import { CustomButton, CustomInput } from '@/shared/components/atoms';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import raabtaLogo from '@/assets/raabtalogo_white_bg.png';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import authService from '../api/auth.service';
import './LoginPage.css'; // Reusing login page CSS for identical UI

// Set Password Validation Schema
const setPasswordSchema = z.object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(6, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});

type SetPasswordFormValues = z.infer<typeof setPasswordSchema>;

export const SetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Multi-tenant: extract school_slug from query params
    const schoolSlug = searchParams.get('school_slug');

    // Extract token or error from URL hash (e.g. #access_token=... or #error=...)
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    const urlParams = useParams<{ token: string }>();
    const token = hashParams.get('access_token') || urlParams.token;
    const hashError = hashParams.get('error_description') || hashParams.get('error');

    // Decode email from JWT if available
    let email = searchParams.get('email') || 'your account';
    if (token && email === 'your account') {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
            const payload = JSON.parse(jsonPayload);
            if (payload.email) email = payload.email;
        } catch (e) {
            // ignore parse error
        }
    }

    const { showToast } = useToastStore();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<SetPasswordFormValues>({
        resolver: zodResolver(setPasswordSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        }
    });

    const onSubmit = async (data: SetPasswordFormValues) => {
        setIsLoading(true);

        try {
            if (!token) {
                showToast('Invalid or missing token in URL.', 'error', 'Error');
                return;
            }
            if (!schoolSlug) {
                showToast('Missing school_slug in URL.', 'error', 'Error');
                return;
            }

            const response = await authService.resetPassword(token, data.password, schoolSlug);

            if (response.success !== false) { // Assuming success might not explicitly be false if it passes, adjust as needed or use response check
                showToast('Password set successfully! You can now log in.', 'success');
                navigate('/login');
            } else {
                showToast(response.error || response.message || 'Failed to set password.', 'error', 'Error');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to set password.';
            showToast(errorMessage, 'error', 'Error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-page-wrapper">
            {/* Visual Background Elements */}
            <div className="login-bg">
                <div className="login-bg__circle login-bg__circle--1" />
                <div className="login-bg__circle login-bg__circle--2" />
                <div className="login-bg__circle login-bg__circle--3" />
            </div>

            {/* Left Side: Branding & Quick Info */}
            <div className="login-info-side">
                <div className="info-content">
                    <div className="brand-header">
                        <div className="brand-logo" style={{ background: 'none', boxShadow: 'none', width: '56px', height: '56px' }}>
                            <img src={raabtaLogo} alt="Raabta Logo" style={{ width: 52, height: 52, objectFit: 'contain', borderRadius: '50%' }} />
                        </div>
                        <span className="brand-name">Raabta</span>
                    </div>

                    <div className="hero-text">
                        <h1 className="hero-title">
                            Set Your <span className="text-gradient">Password.</span>
                        </h1>
                        <p className="hero-subtitle">
                            Please set a secure password to access your institutional portal and start using the system.
                        </p>
                    </div>

                    <div className="features-capsules">
                        <div className="feature-capsule">
                            <ShieldCheck size={16} /> <span>Secure Access</span>
                        </div>
                        <div className="feature-capsule">
                            <Lock size={16} /> <span>Encrypted</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Auth Form */}
            <div className="login-form-side">
                <div className="login-form-container">
                    <div className="login-form-header">
                        <h2 className="login-welcome">Set New Password</h2>
                        <p className="login-instructions">Setting password for: {email}</p>
                    </div>

                    {hashError && (
                        <div style={{ padding: '1rem', marginBottom: '1.5rem', borderRadius: '8px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'var(--status-error)', border: '1px solid rgba(239, 68, 68, 0.3)', fontSize: '0.9rem' }}>
                            <strong>Link Error:</strong> {hashError.replace(/\+/g, ' ')}
                            <br />
                            <small>Please request a new link.</small>
                        </div>
                    )}

                    <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-group">
                            <CustomInput
                                label='New Password'
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                leftIcon={<Lock size={18} />}
                                error={errors.password?.message}
                                rightIcon={
                                    <div
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </div>
                                }
                                {...register('password')}
                            />
                        </div>

                        <div className="form-group" style={{ marginTop: '1rem' }}>
                            <CustomInput
                                label='Confirm Password'
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                leftIcon={<Lock size={18} />}
                                error={errors.confirmPassword?.message}
                                rightIcon={
                                    <div
                                        className="password-toggle"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </div>
                                }
                                {...register('confirmPassword')}
                            />
                        </div>

                        <CustomButton
                            label="Set Password"
                            type="submit"
                            fullWidth
                            size="medium"
                            loading={isLoading}
                            faIcon={faCheckCircle}
                            iconPosition="right"
                            className="login-submit"
                            style={{ marginTop: '2rem' }}
                        />
                    </form>

                    {schoolSlug && (
                        <div className="login-footer">
                            <p>Need help? <button type="button" className="contact-link" onClick={() => navigate('/contact')}>Contact Support</button></p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
